import { Paper, Typography, Table, TableHead, TableBody, TableRow, TableCell,
    Checkbox, Button, Chip, Box } from "@mui/material";
import { useState, useEffect } from "react";
import { useDochazkaQuery, useUlozDochazkuMutation, useUzavritDochazkuMutation,
    useOdemknoutDochazkuMutation }
    from "../../api/JsdhNotifikaceService/service/DochazkaMutations";

interface Props {
    akceId: number;
    dochazkaUzavrenaAt?: string | null;
    datumCasOd: string;
    datumCasDo?: string | null;
}

export default function DochazkaSekce({ akceId, dochazkaUzavrenaAt, datumCasOd, datumCasDo }: Props) {
    const { data } = useDochazkaQuery(akceId);
    const uloz = useUlozDochazkuMutation(akceId);
    const uzavrit = useUzavritDochazkuMutation(akceId);
    const odemknout = useOdemknoutDochazkuMutation(akceId);
    const [stav, setStav] = useState<Record<number, boolean>>({});

    // Use a stable JSON key so the effect doesn't re-fire when the query returns
    // a structurally identical array (new reference each render — common in mocks and stale-time flushes).
    const dataKey = data ? JSON.stringify(data.map((d) => [d.uzivatelId, d.pritomen])) : null;
    useEffect(() => {
        if (data) setStav(Object.fromEntries(data.map((d) => [d.uzivatelId, d.pritomen])));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataKey]);

    const probehla = new Date(datumCasDo || datumCasOd) <= new Date();
    // Uzavrena dochazka je zamcena — BE zapis odmitne 409, takze editaci ani
    // nenabizime. Kdo chce opravit, nejdriv odemkne.
    const zamceno = !!dochazkaUzavrenaAt;

    return (
        <Paper sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">Docházka</Typography>
                <Chip
                    color={zamceno ? "success" : "default"}
                    label={zamceno ? "Docházka uzavřena" : "Docházka odemčena"}
                />
            </Box>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Jméno</TableCell>
                        <TableCell>Byl přítomen</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data?.map((d) => (
                        <TableRow key={d.uzivatelId}>
                            <TableCell>
                                {d.jmeno}{!d.vCiloveSkupine && " (mimo skupinu)"}
                            </TableCell>
                            <TableCell>
                                <Checkbox
                                    checked={!!stav[d.uzivatelId]}
                                    disabled={zamceno}
                                    onChange={(e) => setStav({ ...stav, [d.uzivatelId]: e.target.checked })}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                <Button
                    variant="outlined"
                    disabled={zamceno}
                    onClick={() =>
                        uloz.mutate(
                            Object.entries(stav).map(([uid, p]) => ({ uzivatelId: Number(uid), pritomen: p }))
                        )
                    }
                >
                    Uložit
                </Button>
                {zamceno ? (
                    <Button
                        variant="outlined"
                        color="warning"
                        disabled={odemknout.isPending}
                        onClick={() => odemknout.mutate()}
                    >
                        Odemknout docházku
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        disabled={!probehla || uzavrit.isPending}
                        onClick={() => uzavrit.mutate()}
                    >
                        Uzavřít a propsat
                    </Button>
                )}
            </Box>
        </Paper>
    );
}
